import React, { useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDealsStore } from '../store/dealsStore';
import { mapDealToPhases, extractDocumentStatusWithTypes, calculatePhaseProgress } from '../services/hubspotDealMapper';
import {
  getDealByIdAsync as getDealById,
  getDealContactsAsync as getDealContacts,
  getNacharbeitenStatusAsync as getNacharbeitenStatus,
  getNacharbeitenTicketsAsync,
} from '../services/api/hubspot';
import { getProjektdokumentationTemplate } from '../services/api/projektdokumentation';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentStore } from '../store/documentState';
import { DealContactListItem, HubSpotDeal, NacharbeitenTicketListItem, ProjectStatusPhase } from '../types/hubspot';
import { ProjektdokumentationStoredRecord } from '../types/projektdokumentation';
import PhasePicker from '../components/PhasePicker';
import SyncStatus from '../components/SyncStatus';
import AppHeader from '../components/AppHeader';
import ProjectMilestoneTimeline from '../components/ProjectMilestoneTimeline';

import type { Project, ProjectOverviewStats } from '../interfaces/projects';
import type { MappedPhase } from '../interfaces/phase';

const isOptionalSupportPhase = (phaseId: string): boolean =>
  phaseId.endsWith('-uploads') || phaseId.endsWith('-stoerungen');

const sanitizeAddress = (value: string | null | undefined): string => {
  return String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const normalizePhone = (value: string | null | undefined): string => {
  return String(value || "").replace(/\s+/g, " ").trim();
};

const normalizeTicketText = (value: string | null | undefined): string => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  if (raw.includes("<") && typeof DOMParser !== "undefined") {
    try {
      const parsed = new DOMParser().parseFromString(raw, "text/html");
      const text = parsed.body.textContent || "";
      return text.replace(/\s+/g, " ").trim();
    } catch {
      // Fallback unten greift, falls ein Browser die HTML-Auswertung verweigert.
    }
  }

  return raw
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const toTelHref = (phone: string): string => {
  return `tel:${phone.replace(/[^\d+]/g, "")}`;
};

const formatRelationLabel = (value: string): string => {
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();
};

function buildTimelinePhaseFromProjektdokumentation(
  record: ProjektdokumentationStoredRecord
): ProjectStatusPhase {
  const hasProgressEvidence =
    record.status === 'ready' ||
    record.status === 'in_review' ||
    (record.completenessPercent || 0) > 0;
  const timelineStatus: ProjectStatusPhase['status'] = record.status === 'approved'
    ? 'completed'
    : hasProgressEvidence
      ? 'in_progress'
      : 'open';

  return {
    phaseKey: 'projektdokumentation',
    status: timelineStatus,
    completionPercent: record.status === 'approved' ? 100 : Math.max(0, Math.min(100, record.completenessPercent || 0)),
    missingRequiredCount: record.missingRequiredItems?.length || 0,
    completedAt: record.status === 'approved' ? (record.completedAt || record.updatedAt) : null,
    completedByEmail: record.status === 'approved'
      ? (record.completedByEmail || null)
      : null,
    approvedByEmail: record.status === 'approved'
      ? (record.approvedByEmail || null)
      : null,
  };
}

function mergeTimelinePhasesWithProjektdokumentation(
  currentPhases: ProjectStatusPhase[],
  record: ProjektdokumentationStoredRecord
): ProjectStatusPhase[] {
  const patchedPhase = buildTimelinePhaseFromProjektdokumentation(record);
  const nextPhases = currentPhases.slice();
  const existingIndex = nextPhases.findIndex((phase) => phase.phaseKey === 'projektdokumentation');
  if (existingIndex >= 0) {
    nextPhases[existingIndex] = patchedPhase;
    return nextPhases;
  }
  return [patchedPhase, ...nextPhases];
}

function getPhaseKeyFromPhaseId(
  phaseId: string
): ProjectStatusPhase["phaseKey"] | null {
  if (phaseId.endsWith("-projektdokumentation")) return "projektdokumentation";
  if (phaseId.endsWith("-durchfuehrung")) return "durchfuehrung";
  if (phaseId.endsWith("-inbetriebnahme")) return "inbetriebnahme";
  if (phaseId.endsWith("-abschlussbericht")) return "abschlussbericht";
  return null;
}

const PhasePickerScreen: React.FC = () => {
  const hubspotPortalId = (import.meta.env.VITE_HUBSPOT_PORTAL_ID || '144820044').toString().trim();
  const [project, setProject] = useState<Project | null>(null);
  const [dealContacts, setDealContacts] = useState<DealContactListItem[]>([]);
  const [mappedPhases, setMappedPhases] = useState<MappedPhase[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [, setOverviewStats] = useState<ProjectOverviewStats | null>(null);
  const [nacharbeitenTickets, setNacharbeitenTickets] = useState<NacharbeitenTicketListItem[]>([]);
  const [projectTimelinePhases, setProjectTimelinePhases] = useState<ProjectStatusPhase[]>([]);
  const [projektdokumentationApprovalStatus, setProjektdokumentationApprovalStatus] = useState<"draft" | "in_review" | "ready" | "approved" | null>(null);
  const [mobileHeaderExpanded, setMobileHeaderExpanded] = useState(false);
  const [mobileTimelineExpanded, setMobileTimelineExpanded] = useState(false);
  const [mobileHeaderDetailsOpen, setMobileHeaderDetailsOpen] = useState(false);
  const [desktopHeaderDetailsOpen, setDesktopHeaderDetailsOpen] = useState(false);
  const [expandedNacharbeitenTicketIds, setExpandedNacharbeitenTicketIds] = useState<Record<string, boolean>>({});

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { setHubSpotDocumentStatus } = useDocumentStore();
  const { upsertDeal, fetchDeals, deals } = useDealsStore();

  // Hintergrund-Fetch: Falls nur ein einzelner Deal im Store ist (z.B. nach Page-Refresh auf
  // der Detailseite), werden alle Deals im Hintergrund geladen, damit die Liste beim
  // Zurücknavigieren vollständig ist. Nur wenn online.
  useEffect(() => {
    if (!navigator.onLine) return;
    if (deals.length > 1) return;
    void fetchDeals({ restoreUnfilteredFromCache: true });
  }, [deals.length, fetchDeals]);

  useEffect(() => {
    const loadProject = async () => {
      if (!id) {
        setError('Projekt-ID fehlt in der URL');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const deals = useDealsStore.getState().deals;
        let deal: HubSpotDeal | null | undefined = deals.find((d) => d.id === id);
        // Lazy Loading: Falls Deal nicht im initialen Cache (z.B. Default-Limit), gezielt per ID laden.
        if (!deal) {
          const fetchedDeal = await getDealById(id);
          if (fetchedDeal) {
            deal = fetchedDeal;
            upsertDeal(fetchedDeal);
          }
        }

        if (!deal) {
          throw new Error('Projekt nicht gefunden');
        }
        let resolvedDeal: HubSpotDeal = deal;
        let nextTimelinePhases = resolvedDeal.project_status?.phases || [];
        setProjektdokumentationApprovalStatus(resolvedDeal.project_status?.projektdokumentationApprovalStatus || null);
        setProjectTimelinePhases(nextTimelinePhases);

        setProject({
          _id: resolvedDeal.id,
          name: resolvedDeal.dealname || 'Unbekanntes Projekt',
          kunde: 'HubSpot Deal',
          adresse: sanitizeAddress(
            resolvedDeal.objekt___anschrift ||
              [
                [resolvedDeal.contact?.zip, resolvedDeal.contact?.city].filter(Boolean).join(' '),
                resolvedDeal.contact?.address,
              ].filter(Boolean).join(', ')
          ),
          projektStatus: 'In Bearbeitung',
        });

        const phases = mapDealToPhases(resolvedDeal);
        setMappedPhases(phases);
        const corePhases = phases.filter((phase) => !isOptionalSupportPhase(phase._id));

        for (const phase of phases) {
          const progress = calculatePhaseProgress(phase._id, resolvedDeal);
          const documents = extractDocumentStatusWithTypes(resolvedDeal, phase._id);
          setHubSpotDocumentStatus?.(phase._id, documents, progress);
        }

        const phaseStateSnapshot = useDocumentStore.getState().phases;
        const progressSnapshot = useDocumentStore.getState().progress;
        const projectStatusPhaseMap = new Map(
          (resolvedDeal.project_status?.phases || []).map((entry) => [entry.phaseKey, entry])
        );

        const effectiveTimelinePhases: ProjectStatusPhase[] = corePhases
          .map((phase) => {
            const phaseKey = getPhaseKeyFromPhaseId(phase._id);
            if (!phaseKey) return null;
            const persistedPhaseState = phaseStateSnapshot[phase._id];
            const fallbackProgress = calculatePhaseProgress(phase._id, resolvedDeal);
            const storeProgress = progressSnapshot[phase._id];
            const effectiveProgress = storeProgress || fallbackProgress;
            const existingProjectStatusPhase = projectStatusPhaseMap.get(phaseKey);
            const isCompletedByProgress =
              effectiveProgress.required > 0 &&
              effectiveProgress.completed >= effectiveProgress.required;
            const isCompleted =
              persistedPhaseState?.status === "completed" ||
              phase.status === "completed" ||
              isCompletedByProgress;
            const completionPercent = effectiveProgress.required > 0
              ? Math.round((effectiveProgress.completed / effectiveProgress.required) * 100)
              : (existingProjectStatusPhase?.completionPercent || 0);

            return {
              phaseKey,
              status: isCompleted
                ? "completed"
                : completionPercent > 0
                  ? "in_progress"
                  : "open",
              completionPercent,
              missingRequiredCount: Math.max(
                (effectiveProgress.required || 0) - (effectiveProgress.completed || 0),
                0
              ),
              completedAt:
                persistedPhaseState?.completedAt ||
                existingProjectStatusPhase?.completedAt ||
                null,
              completedByEmail:
                phase._id.endsWith("-projektdokumentation")
                  ? existingProjectStatusPhase?.completedByEmail || null
                  : persistedPhaseState?.completedByEmail ||
                    existingProjectStatusPhase?.completedByEmail ||
                    null,
              approvedByEmail: existingProjectStatusPhase?.approvedByEmail || null,
            } as ProjectStatusPhase;
          })
          .filter((phase): phase is ProjectStatusPhase => Boolean(phase));

        nextTimelinePhases = effectiveTimelinePhases;
        setProjectTimelinePhases(nextTimelinePhases);

        let totalRequired = 0;
        let totalCompleted = 0;
        let completedPhases = 0;
        corePhases.forEach((phase) => {
          const fallbackProgress = calculatePhaseProgress(phase._id, resolvedDeal);
          const storeProgress = progressSnapshot[phase._id];
          const effectiveProgress = storeProgress || fallbackProgress;
          totalRequired += effectiveProgress.required;
          totalCompleted += effectiveProgress.completed;

          const persistedPhaseState = phaseStateSnapshot[phase._id];
          const isCompletedByProgress =
            effectiveProgress.required > 0 &&
            effectiveProgress.completed >= effectiveProgress.required;
          if (persistedPhaseState?.status === "completed" || phase.status === "completed" || isCompletedByProgress) {
            completedPhases += 1;
          }
        });

        const nextPhase = corePhases.find((phase) => {
          const fallbackProgress = calculatePhaseProgress(phase._id, resolvedDeal);
          const storeProgress = progressSnapshot[phase._id];
          const effectiveProgress = storeProgress || fallbackProgress;
          const persistedPhaseState = phaseStateSnapshot[phase._id];
          const isCompletedByProgress =
            effectiveProgress.required > 0 &&
            effectiveProgress.completed >= effectiveProgress.required;
          const isCompleted =
            persistedPhaseState?.status === "completed" ||
            phase.status === "completed" ||
            isCompletedByProgress;
          return !isCompleted;
        });

        setOverviewStats({
          totalPhases: corePhases.length,
          completedPhases,
          requiredDocuments: totalRequired,
          completedDocuments: totalCompleted,
          nextPhaseName: nextPhase?.name || 'Alles abgeschlossen',
        });

        if (!navigator.onLine) {
          return;
        }

        const [contacts, tickets, nacharbeitenStatus, projektdokumentationTemplate] = await Promise.all([
          getDealContacts(id),
          getNacharbeitenTicketsAsync(id).catch(() => [] as NacharbeitenTicketListItem[]),
          getNacharbeitenStatus(id).catch(() => null),
          getProjektdokumentationTemplate(id).catch(() => null),
        ]);
        setDealContacts(
          contacts.map((contact) => ({
            ...contact,
            relations: Array.from(
              new Set(
                (contact.relations || [])
                  .map((relation) => relation.trim())
                  .filter((relation) => relation.length > 0)
              )
            ),
            phones: (contact.phones || [])
              .map((entry) => ({
                ...entry,
                number: normalizePhone(entry.number),
              }))
              .filter((entry) => entry.number.length > 0),
          }))
        );
        const projektdokumentationRecord = projektdokumentationTemplate?.existingRecord || null;
        if (projektdokumentationRecord) {
          nextTimelinePhases = mergeTimelinePhasesWithProjektdokumentation(nextTimelinePhases, projektdokumentationRecord);
          setProjektdokumentationApprovalStatus(projektdokumentationRecord.status || null);
          setProjectTimelinePhases(nextTimelinePhases);
        }

        if (nacharbeitenStatus || projektdokumentationRecord) {
          resolvedDeal = {
            ...resolvedDeal,
            project_status: resolvedDeal.project_status
              ? {
                  ...resolvedDeal.project_status,
                  phases: nextTimelinePhases,
                  nacharbeiten: nacharbeitenStatus || resolvedDeal.project_status.nacharbeiten,
                  projektdokumentationApprovalStatus: projektdokumentationRecord?.status || resolvedDeal.project_status.projektdokumentationApprovalStatus || null,
                }
              : resolvedDeal.project_status,
          };
          upsertDeal(resolvedDeal);
        }
        setNacharbeitenTickets(
          tickets.slice().sort((left, right) => {
            const leftTs = Date.parse(left.lastModifiedAt || left.createdAt || '') || 0;
            const rightTs = Date.parse(right.lastModifiedAt || right.createdAt || '') || 0;
            return rightTs - leftTs;
          })
        );
        setExpandedNacharbeitenTicketIds({});
      } catch (err: any) {
        setError(err.message || 'Fehler beim Laden des Projekts');
      } finally {
        setLoading(false);
      }
    };

    void loadProject();
  }, [id, setHubSpotDocumentStatus, upsertDeal]);

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const toggleNacharbeitenTicket = (ticketId: string) => {
    setExpandedNacharbeitenTicketIds((current) => ({
      ...current,
      [ticketId]: !current[ticketId],
    }));
  };

  const statusBadge = useMemo(() => {
    const baseClasses = 'inline-flex items-center rounded-full px-3 py-1.5 text-xs font-semibold';
    switch (project?.projektStatus) {
      case 'Geplant':
        return <span className={`${baseClasses} bg-amber-100 text-amber-900`}>Geplant</span>;
      case 'Abgeschlossen':
        return <span className={`${baseClasses} bg-emerald-100 text-emerald-900`}>Abgeschlossen</span>;
      default:
        return <span className={`${baseClasses} bg-cyan-100 text-cyan-900`}>In Bearbeitung</span>;
    }
  }, [project?.projektStatus]);

  const openAppleMaps = (address: string) => {
    const encoded = encodeURIComponent(address);
    const appUrl = `maps://?daddr=${encoded}&dirflg=d`;
    const webUrl = `https://maps.apple.com/?daddr=${encoded}&dirflg=d`;
    window.location.href = appUrl;
    setTimeout(() => window.open(webUrl, '_blank', 'noopener,noreferrer'), 600);
  };

  const openGoogleMaps = (address: string) => {
    const encoded = encodeURIComponent(address);
    const appUrl = `comgooglemaps://?daddr=${encoded}&directionsmode=driving`;
    const webUrl = `https://www.google.com/maps/dir/?api=1&destination=${encoded}&travelmode=driving`;
    window.location.href = appUrl;
    setTimeout(() => window.open(webUrl, '_blank', 'noopener,noreferrer'), 600);
  };

  const openHubSpotDeal = (dealId: string) => {
    if (!dealId) return;
    const hubspotUrl = `https://app.hubspot.com/contacts/${hubspotPortalId}/record/0-3/${encodeURIComponent(dealId)}`;
    window.open(hubspotUrl, '_blank', 'noopener,noreferrer');
  };

  const navigateToProjectsOverview = () => {
    navigate('/projects', { replace: true });
  };
  const mobileContactCountLabel =
    dealContacts.length === 1 ? '1 Kontakt' : `${dealContacts.length} Kontakte`;

  return (
    <div className="min-h-screen bg-transparent">
      <AppHeader
        title={loading ? 'Projekt wird geladen...' : project?.name || 'Projektphasen'}
        eyebrow="Projekt Cockpit"
        useDesktopLogoOffset={false}
        mobileCollapsible
        mobileExpanded={mobileHeaderExpanded}
        onTitleClick={() => setMobileHeaderExpanded((current) => !current)}
        meta={(
          <>
            <span>{project?.kunde}</span>
            {project && statusBadge}
          </>
        )}
        actions={project?._id ? (
          <button
            type="button"
            onClick={() => openHubSpotDeal(project._id)}
            className="hidden h-11 items-center rounded-full border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:text-slate-950 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-white/20 dark:hover:text-white sm:inline-flex"
          >
            In HubSpot öffnen
          </button>
        ) : undefined}
        details={project ? (
          <div className="space-y-2">
            {project?._id && (
              <button
                type="button"
                onClick={() => openHubSpotDeal(project._id)}
                className="inline-flex h-10 items-center rounded-full border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:text-slate-950 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-white/20 dark:hover:text-white sm:hidden"
              >
                In HubSpot öffnen
              </button>
            )}
            <button
              type="button"
              onClick={() => setMobileHeaderDetailsOpen((current) => !current)}
              className="inline-flex min-h-[40px] w-full items-center justify-between rounded-xl border border-slate-200 bg-white/90 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:text-slate-900 dark:border-white/15 dark:bg-slate-900/70 dark:text-slate-200 dark:hover:border-white/30 sm:hidden"
              aria-expanded={mobileHeaderDetailsOpen}
              aria-controls="project-header-mobile-details"
            >
              <span className="inline-flex items-center gap-2">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M3 5h18l-7 8v5l-4 2v-7L3 5z" />
                </svg>
                Kontakte & Navigation
              </span>
              <span className="inline-flex items-center gap-2 text-slate-500 dark:text-slate-400">
                {dealContacts.length > 0 ? mobileContactCountLabel : 'Keine Kontakte'}
                <svg
                  className={`h-4 w-4 transition-transform ${mobileHeaderDetailsOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M19 9l-7 7-7-7" />
                </svg>
              </span>
            </button>
            <button
              type="button"
              onClick={() => setDesktopHeaderDetailsOpen((current) => !current)}
              className="hidden min-h-[40px] w-full items-center justify-between rounded-xl border border-slate-200 bg-white/90 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:text-slate-900 dark:border-white/15 dark:bg-slate-900/70 dark:text-slate-200 dark:hover:border-white/30 sm:inline-flex"
              aria-expanded={desktopHeaderDetailsOpen}
              aria-controls="project-header-desktop-details"
            >
              <span className="inline-flex items-center gap-2">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M3 5h18l-7 8v5l-4 2v-7L3 5z" />
                </svg>
                Kontakte & Navigation
              </span>
              <span className="inline-flex items-center gap-2 text-slate-500 dark:text-slate-400">
                {dealContacts.length > 0 ? mobileContactCountLabel : 'Keine Kontakte'}
                <svg
                  className={`h-4 w-4 transition-transform ${desktopHeaderDetailsOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M19 9l-7 7-7-7" />
                </svg>
              </span>
            </button>

            <div
              id="project-header-mobile-details"
              className={`${mobileHeaderDetailsOpen ? 'block' : 'hidden'} sm:hidden`}
            >
              <div className="rounded-2xl border border-slate-200 bg-white/80 shadow-sm dark:border-white/10 dark:bg-slate-900/70">
                <div className="border-b border-slate-200/80 px-3 py-2.5 dark:border-white/10">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Adresse</p>
                  <p className="mt-1.5 text-sm leading-snug text-slate-700 dark:text-slate-200">{project.adresse || "Keine Adresse hinterlegt"}</p>
                  <div className="mt-2.5 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => project.adresse && openAppleMaps(project.adresse)}
                      disabled={!project.adresse}
                      className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-[11px] font-semibold text-slate-700 transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/20 dark:bg-slate-900 dark:text-slate-200"
                    >
                      Apple Maps
                    </button>
                    <button
                      type="button"
                      onClick={() => project.adresse && openGoogleMaps(project.adresse)}
                      disabled={!project.adresse}
                      className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-[11px] font-semibold text-slate-700 transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/20 dark:bg-slate-900 dark:text-slate-200"
                    >
                      Google Maps
                    </button>
                  </div>
                </div>

                <div className="px-3 py-2.5">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Kontakte</p>
                  {dealContacts.length === 0 ? (
                    <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">Keine Kontakt-Telefonnummern hinterlegt.</p>
                  ) : (
                    <ul className="mt-2 space-y-2">
                      {dealContacts.map((contact) => (
                        <li key={contact.id} className="rounded-xl border border-slate-200 bg-slate-50/80 px-2.5 py-2 dark:border-white/10 dark:bg-slate-900/60">
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{contact.fullName}</p>
                              {contact.relations.length > 0 && (
                                <p className="mt-0.5 truncate text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500 dark:text-slate-400">
                                  {contact.relations.map((relation) => formatRelationLabel(relation)).join(' · ')}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="mt-1.5 space-y-1.5">
                            {contact.phones.length === 0 ? (
                              <p className="text-xs text-slate-500 dark:text-slate-400">Keine Telefonnummer hinterlegt</p>
                            ) : (
                              contact.phones.map((entry, index) => (
                                <div key={`${contact.id}-${entry.label}-${entry.number}-${index}`} className="flex items-center justify-between gap-2">
                                  <p className="min-w-0 text-xs text-slate-600 dark:text-slate-300">
                                    <span className="font-medium text-slate-700 dark:text-slate-200">{entry.label}:</span>{' '}
                                    <span className="truncate">{entry.number}</span>
                                  </p>
                                  <button
                                    type="button"
                                    onClick={() => window.location.assign(toTelHref(entry.number))}
                                    aria-label={`${entry.label} anrufen: ${entry.number}`}
                                    title={`${entry.label} anrufen`}
                                    className="shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-700 transition hover:border-slate-400 hover:text-slate-900 dark:border-white/20 dark:bg-slate-900 dark:text-slate-200"
                                  >
                                    <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M22 16.92v2a2 2 0 01-2.18 2 19.78 19.78 0 01-8.63-3.07A19.5 19.5 0 015.15 12.8 19.78 19.78 0 012.08 4.1 2 2 0 014.06 2h2a2 2 0 012 1.72c.12.9.33 1.78.62 2.62a2 2 0 01-.45 2.11L7.1 9.6a16 16 0 007.3 7.3l1.15-1.15a2 2 0 012.11-.45c.84.29 1.72.5 2.62.62A2 2 0 0122 16.92z" />
                                    </svg>
                                  </button>
                                </div>
                              ))
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>

            <div
              id="project-header-desktop-details"
              className={`${desktopHeaderDetailsOpen ? 'hidden gap-2 sm:grid sm:grid-cols-2 xl:grid-cols-3' : 'hidden'}`}
            >
              <div className="rounded-2xl border border-slate-200 bg-white/70 px-4 py-3">
                <p className="text-sm font-semibold text-slate-900">Adresse & Navigation</p>
                <p className="mt-2 text-sm text-slate-600">
                  {project.adresse || "Keine Adresse hinterlegt"}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => project.adresse && openAppleMaps(project.adresse)}
                    disabled={!project.adresse}
                    className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Apple Maps
                  </button>
                  <button
                    type="button"
                    onClick={() => project.adresse && openGoogleMaps(project.adresse)}
                    disabled={!project.adresse}
                    className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Google Maps
                  </button>
                </div>
              </div>

              {dealContacts.length === 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-white/70 px-4 py-3 text-sm text-slate-500 sm:col-span-1 xl:col-span-2">
                  Keine Kontakt-Telefonnummern hinterlegt.
                </div>
              ) : (
                dealContacts.map((contact) => (
                  <div key={contact.id} className="rounded-2xl border border-slate-200 bg-white/70 px-4 py-3">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-slate-900">{contact.fullName}</p>
                      {contact.relations.length > 0 && (
                        <div className="flex flex-wrap justify-end gap-1">
                          {contact.relations.map((relation) => (
                            <span
                              key={`${contact.id}-${relation}`}
                              className="inline-flex items-center rounded-md bg-slate-200 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-slate-700"
                            >
                              {formatRelationLabel(relation)}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="mt-2 space-y-2">
                      {contact.phones.length === 0 ? (
                        <p className="text-sm text-slate-500">Keine Telefonnummer hinterlegt</p>
                      ) : (
                        contact.phones.map((entry, index) => (
                          <div key={`${contact.id}-${entry.label}-${entry.number}-${index}`} className="flex items-center justify-between gap-6">
                            <p className="min-w-0 pr-3 text-sm text-slate-600">
                              <span className="font-medium text-slate-700">{entry.label}:</span>{" "}
                              <span className="truncate">{entry.number}</span>
                            </p>
                            <button
                              type="button"
                              onClick={() => window.location.assign(toTelHref(entry.number))}
                              aria-label={`${entry.label} anrufen: ${entry.number}`}
                              title={`${entry.label} anrufen`}
                              className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-700 transition hover:border-slate-400 hover:text-slate-900"
                            >
                              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M22 16.92v2a2 2 0 01-2.18 2 19.78 19.78 0 01-8.63-3.07A19.5 19.5 0 015.15 12.8 19.78 19.78 0 012.08 4.1 2 2 0 014.06 2h2a2 2 0 012 1.72c.12.9.33 1.78.62 2.62a2 2 0 01-.45 2.11L7.1 9.6a16 16 0 007.3 7.3l1.15-1.15a2 2 0 012.11-.45c.84.29 1.72.5 2.62.62A2 2 0 0122 16.92z" />
                              </svg>
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        ) : undefined}
        backAction={{ onClick: navigateToProjectsOverview }}
        menuContent={(
          <>
            <button
              onClick={() => navigate('/projects')}
              className="mt-1 flex w-full items-center rounded-xl px-3 py-3 text-left text-sm text-slate-700 transition hover:bg-slate-50"
            >
              <svg className="mr-3 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              Zur Projekt-Übersicht
            </button>
            <button
              onClick={logout}
              className="flex w-full items-center rounded-xl px-3 py-3 text-left text-sm text-red-600 transition hover:bg-red-50"
            >
              <svg className="mr-3 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Abmelden
            </button>
          </>
        )}
      />

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 pb-[calc(env(safe-area-inset-bottom)+80px)] space-y-6">
        {error && (
          <div className="rounded-[24px] border border-red-200 bg-red-50 p-4 text-red-800 dark:border-red-400/40 dark:bg-red-950/40 dark:text-red-200">
            {error}
          </div>
        )}

        {loading && !error ? (
          <div className="flex justify-center items-center py-12">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-cyan-600" />
          </div>
        ) : (
          <>
            <section className="rounded-3xl border border-slate-200 bg-white px-5 py-5 shadow-sm dark:border-white/10 dark:bg-slate-900/80 dark:shadow-[0_20px_60px_rgba(2,6,23,0.45)]">
              <button
                type="button"
                onClick={() => setMobileTimelineExpanded((current) => !current)}
                className="flex w-full items-center justify-between gap-2 text-left sm:pointer-events-none"
                aria-expanded={mobileTimelineExpanded}
                aria-controls="project-timeline-mobile-content"
              >
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">Projekt-Zeitstrahl</h2>
                <svg
                  className={`h-4 w-4 shrink-0 text-slate-400 transition-transform sm:hidden ${mobileTimelineExpanded ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div
                id="project-timeline-mobile-content"
                className={`${mobileTimelineExpanded ? 'block' : 'hidden'} sm:block`}
              >
                <ProjectMilestoneTimeline
                  phases={projectTimelinePhases}
                  projektdokumentationApprovalStatus={projektdokumentationApprovalStatus}
                  showMilestoneText
                  doneBadgeClassName="lg:-ml-3 lg:relative lg:-top-2"
                  className="mt-3 border-transparent bg-transparent px-0 py-0 shadow-none dark:bg-transparent"
                />
              </div>
            </section>
            <PhasePicker
              onError={handleError}
              phases={mappedPhases}
              useProvidedPhasesOnly
            />
            <section className="rounded-3xl border border-slate-200 bg-white px-5 py-5 shadow-sm dark:border-white/10 dark:bg-slate-900/80 dark:shadow-[0_20px_60px_rgba(2,6,23,0.45)]">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">Nacharbeiten</h2>
              {nacharbeitenTickets.length === 0 ? (
                <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">Keine Nacharbeiten-Tickets vorhanden.</p>
              ) : (
                <ul className="mt-3 space-y-2">
                  {nacharbeitenTickets.map((ticket) => {
                    const isExpanded = Boolean(expandedNacharbeitenTicketIds[ticket.id]);
                    const title = normalizeTicketText(ticket.title) || "Kein Titel hinterlegt";
                    const description = normalizeTicketText(ticket.description) || "Keine Ticketbeschreibung hinterlegt.";
                    const detailsId = `nacharbeiten-ticket-${ticket.id}-details`;

                    return (
                      <li key={ticket.id} className="rounded-xl border border-slate-100 px-3 py-2 dark:border-white/10 dark:bg-slate-950/70">
                        <div className="flex items-start justify-between gap-3">
                          <button
                            type="button"
                            onClick={() => toggleNacharbeitenTicket(ticket.id)}
                            aria-expanded={isExpanded}
                            aria-controls={detailsId}
                            className="flex min-w-0 flex-1 items-start gap-3 text-left"
                          >
                            <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-500 dark:border-white/15 dark:text-slate-300">
                              <svg
                                className={`h-3.5 w-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                              </svg>
                            </span>
                            <span className="min-w-0">
                              <span className="block truncate text-sm font-medium text-slate-900 dark:text-slate-100">Ticket #{ticket.id}</span>
                              <span className="block text-xs text-slate-500 dark:text-slate-400">{ticket.open ? 'Offen' : 'Geschlossen'}</span>
                              {ticket.submittedByEmail && (
                                <span className="block truncate text-xs text-slate-500 dark:text-slate-400">
                                  Gemeldet von {ticket.submittedByEmail}
                                </span>
                              )}
                            </span>
                          </button>
                          <a
                            href={`https://app.hubspot.com/contacts/${hubspotPortalId}/record/0-5/${encodeURIComponent(ticket.id)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="shrink-0 text-sm font-semibold text-slate-700 underline decoration-slate-300 underline-offset-2 hover:text-slate-950 dark:text-slate-200 dark:decoration-slate-500 dark:hover:text-white"
                          >
                            In HubSpot öffnen
                          </a>
                        </div>
                        {isExpanded && (
                          <div id={detailsId} className="mt-3 grid gap-3 border-t border-slate-100 pt-3 text-sm dark:border-white/10">
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Titel</p>
                              <p className="mt-1 text-slate-900 dark:text-slate-100">{title}</p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Ticketbeschreibung</p>
                              <p className="mt-1 whitespace-pre-wrap text-slate-700 dark:text-slate-300">{description}</p>
                            </div>
                            {ticket.submittedByEmail && (
                              <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Eingereicht von</p>
                                <p className="mt-1 text-slate-700 dark:text-slate-300">{ticket.submittedByEmail}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </>
        )}
      </main>

      <SyncStatus />
    </div>
  );
};

export default PhasePickerScreen;
